/* FavoriteFoodsList — любими храни с персонализирани индекси (iter420.2 Phase 2).
 *
 * Показва запазените `favorite_food` snapshots. Всеки food има computed
 * индекси (NIS%, DIL, fiber, anti-inflammatory hint) запазени в payload.
 *
 * Empty state — насочващо съобщение към MealLog/FoodSearchOverlay.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Loader2, Flame, Activity, Leaf, TrendingDown, Trash2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";

function FoodChip({ icon: Icon, label, value, suffix, color }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: `${color}22`, color }}>
      <Icon className="w-3 h-3" />
      {value}{suffix} {label}
    </span>
  );
}

export default function FavoriteFoodsList({ isDark, bg }) {
  const { API } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-intelligence/favorites`);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/health-intelligence/snapshot/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success(bg ? "Изтрито" : "Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}
          data-testid="hi-favorites">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Heart className={`w-4 h-4 ${isDark ? "text-rose-300" : "text-rose-500"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Моите любими храни" : "My favorite foods"}
          </h3>
          {items.length > 0 && (
            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${
                  isDark ? "bg-rose-500/20 text-rose-200" : "bg-rose-100 text-rose-700"
                }`}>
              {items.length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className={`w-5 h-5 animate-spin ${isDark ? "text-rose-300" : "text-rose-500"}`} />
          </div>
        ) : items.length === 0 ? (
          <div className={`py-6 text-center ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            <Heart className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-xs leading-relaxed max-w-xs mx-auto">
              {bg
                ? 'В дневника на хранене натисни "⭐ Запази" до храна за да я добавиш тук с нейните персонални индекси.'
                : 'In the meal diary press "⭐ Save" next to a food to add it here with its personal indices.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const p = it.payload || {};
              return (
                <div key={it.id}
                     data-testid={`hi-favorite-${it.id}`}
                     className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${
                       isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-slate-50/60"
                     }`}>
                  <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-500 text-white`}>
                    <Heart className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-xs font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}>
                      {it.title}
                    </h4>
                    {it.summary && (
                      <p className={`text-[10.5px] mt-0.5 line-clamp-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {it.summary}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {typeof p.nis_pct === "number" && (
                        <FoodChip icon={Activity} label="NIS" value={Math.round(p.nis_pct)} suffix="%" color="#8B5CF6" />
                      )}
                      {typeof p.dil === "number" && (
                        <FoodChip icon={Flame} label="DIL" value={Math.round(p.dil)} suffix="g" color="#F59E0B" />
                      )}
                      {typeof p.fiber_g === "number" && p.fiber_g > 0 && (
                        <FoodChip icon={Leaf} label={bg ? "фибри" : "fiber"} value={Math.round(p.fiber_g)} suffix="g" color="#10B981" />
                      )}
                      {p.anti_inflammatory && (
                        <FoodChip icon={TrendingDown} label={bg ? "възпаление" : "inflam."} value="↓" suffix="" color="#0EA5E9" />
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(it.id)}
                    disabled={deletingId === it.id}
                    data-testid={`hi-favorite-delete-${it.id}`}
                    title={bg ? "Премахни от любими" : "Remove from favorites"}
                    className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      isDark ? "text-slate-500 hover:text-rose-300 hover:bg-rose-500/10" : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                    }`}>
                    {deletingId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
